import SugarRendererV0 from '../../renderers/SugarRendererV0/index';
import SugarRendererV1 from '../../renderers/SugarRendererV1/index';
import ThumbgenRenderer from "../../renderers/ThumbgenRenderer/index";
import {
    disableCanvasSmoothing,
    createCanvas,
    numericSize,
    cacheGet,
    cacheSet,
    error,
    log,
    RENDERER_TYPE_V1,
    RENDERER_TYPE_THUMBGEN,
    RENDERER_TYPE_V0
} from "../../utils/utils";
import {assetsCDN} from "../../utils/globals";

/**
 * @class SugarWrapper
 */
export default class SugarWrapper {

    env = 'prod';
    size = "small";
    surface = null;
    layoutId = null;
    layoutVersion = null;
    sizeId = null;
    occasionId = null;
    styleId = null;
    brand = null;
    addons = null;
    childSkus = null;
    defaultFoilColor = null
    isLandscape = false;
    forceOutputScaling = false;
    renderer = "v0";
    debug = false;
    project = {};
    layout = {};
    designJson = {};
    thumbParams = {};

    /**
     * Constructing a new instance of SugarWrapper
     * @memberOf SugarWrapper
     *
     * @param {{sizeId: *, childSkus: {}, surface: *, svg: (null|string|SVGSVGElement|String|*), env: *, layoutId: *, layoutVersion: *, layoutName: (null|*|string), isLandscape: (*|boolean), size: *, styleId: *, occasionId: (null|*|number), brand: *}} params
     * @example
     * //the example's surface is a base 64 image 1x1 for saving space
     * const wrapper = new SugarWrapper( {
     *     env: 'prod',
           layoutId: 142881,
           layoutVersion: 7,
           sizeId: 122,
           styleId: 3771,
           occasionId: 38,
           brand: "SFLY",
           childSkus: {"PILLOW_FABRIC": "PILLOW04"},
           surface:"data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
     * } );
     */
    constructor(params) {
        Object.assign(this, params);
        this.validate();

        if (this.debug) {
            log(`initiated SugarWrapper`, this);
        }

    }

    /**
     * Validates the initialized input
     * @private
     * @memberOf SugarWrapper
     */
    validate() {
        if (this.renderer !== RENDERER_TYPE_THUMBGEN && Object.keys(this.project).length > 0) {
            if (this.surface === null || (typeof this.surface === "object" && this.surface.constructor.name !== "HTMLCanvasElement")) {
                error("Validation Error", "input surface must be a canvas or base64 image");
            }

            if (!this.layoutId) {
                error("Validation Error", "layoutId is not a number");
            }

            if (!this.layoutVersion) {
                error("Validation Error", "layoutVersion is not a number");
            }

            if (!this.sizeId) {
                error("Validation Error", "sizeId is not a number");
            }

            if (!this.occasionId) {
                error("Validation Error", "occasionId is not a number");
            }

            if (!this.styleId) {
                error("Validation Error", "styleId is not a number");
            }

            if (!this.brand) {
                error("Validation Error", "brand is empty");
            }

            if (typeof this.childSkus !== "object" || this.childSkus === null) {
                error("Validation Error", "childSkus is not an object");
            }
        }

    }

    /**
     * Generating the product
     * @public
     * @memberOf SugarWrapper
     * @return HTMLCanvasElement
     * @example
     * wrapper.generate().then(canvas => {
     *     console.log(canvas);
     * });
     */
    async generate() {
        try {
            let renderer = null;
            switch (this.renderer) {
                case RENDERER_TYPE_THUMBGEN:
                    renderer = new ThumbgenRenderer(this.env, this.userId, this.project, this.designJson, this.layout, this.thumbParams);
                    break;
                case RENDERER_TYPE_V1:
                    const orientation = this.layoutName && this.layoutName.includes("_P_") ? "P" :
                        this.layoutName && this.layoutName.includes("_L_") ? "L" : null;
                    const sugarV1Map = cacheGet("mapping").sugarIds;
                    const sugarV1Id = this.findInMap(sugarV1Map, this.sizeId, orientation,
                        this.addons && Object.keys(this.addons).length !== 0 ?
                            Object.keys(this.addons).map(key => this.addons[key]) : null);
                    renderer = new SugarRendererV1(this.env, this.surface, sugarV1Id,
                        numericSize(this.size, true), this.addons);
                    break;
                default:
                case RENDERER_TYPE_V0:
                    let svg = null;
                    try {
                        svg = await this.getSVG();
                    } catch (e) {
                        error("SugarWrapper", "failed to get svg");
                    }
                    renderer = new SugarRendererV0(this.env, this.surface, svg, this.defaultFoilColor);
                    break;
            }

            if (renderer) {
                try {
                    return this.output(await renderer.render());
                } catch (e) {
                    error("Renderer", `Failed to render - ${e}`);
                }
            } else {
                error("SugarWrapper:: Generate", `failed to match renderer`);
            }

        } catch (e) {
            error("SugarWrapper:: Generate", `failed to render - ${e.message}`);
        }
    }


    /**
     * Find the Id of sugar data in sugar map
     * @param sugarMap
     * @param sizeId
     * @param orientation
     * @param addons
     * @returns {*}
     */
    findInMap(sugarMap, sizeId, orientation, addons) {
        let res = null;
        const sizeSugarData = sugarMap && typeof sugarMap[sizeId] === 'object' && sugarMap[sizeId]
        const webSizeSugarData = sizeSugarData && sizeSugarData.filter(el => el.platforms.includes("web"))
        if (typeof webSizeSugarData === 'object' && webSizeSugarData.length > 0) {
            const orientation_res = orientation && webSizeSugarData.filter(el => el.orientation === orientation);
            // If there is only one orientation use it
            if (orientation_res && orientation_res.length === 1 && typeof orientation_res[0] === 'object' && orientation_res[0]?.id) {
                res = orientation_res[0].id;
            }
            // Addons
            else if (addons) {
                // If there is orientation use it otherwise take all
                const mapItems = orientation_res && orientation_res.length > 0 ? orientation_res : webSizeSugarData;
                res = this.findByAddons(mapItems, addons);
            }
            // If there is more than one with orientation but there is no addons
            else if (orientation_res && orientation_res.length !== 0) {
                return this.findByDefault(orientation_res)
            }
            // Default, take first id from array
            else {
                return this.findByDefault(webSizeSugarData)
            }
            if (typeof res === 'string') {
                return res
            }
        } else {
            error("Bad Input", "input is wrong or insufficient to determine the answer");
        }
        if (!res) {
            // return default value for this sizeId
            this.findByDefault(sizeSugarData)
        }
        return res;
    };

    /**
     * Return the first id from sugarData
     * @param sugarData
     * @returns {string}
     */
    findByDefault(sugarData) {
        const res = typeof sugarData[0] === 'object' && sugarData[0].id
            ? sugarData[0].id
            : null
        if (typeof res === 'string') {
            return res;
        } else {
            error("Bad Input", "input is wrong or insufficient to determine the answer");
        }
    };

    /**
     * Find the sugarId by addons parameter
     * @param mapItems
     * @param addons
     * @returns {null|*}
     */
    findByAddons(mapItems, addons) {
        const addons_res = this.hasMostElements(mapItems, addons);
        if (typeof addons_res === 'object' && addons_res?.id) {
            return addons_res.id;
        } else {
            // In case we don't have items matching the addons take the one without options
            const noOptionsItem = mapItems.find(items => !items.options);
            if (noOptionsItem?.id) {
                return noOptionsItem.id;
            }
        }
        return null;
    };

    /**
     * Find what array has most options as target
     * @param array_of_objects
     * @param target
     * @returns {null|*}
     */
    hasMostElements(array_of_objects, target) {
        let hasAtLeastOneMatchOption = false;
        const objects = array_of_objects.map(obj => {
            const count = obj["options"] ? this.getSimilarElements(obj["options"], target) : 0;
            if (count > 0) {
                hasAtLeastOneMatchOption = true;
            }
            return {"obj": obj, "count": count};
        });
        if (objects.length > 0 && hasAtLeastOneMatchOption) {
            return objects.reduce((prev, current) => (prev.count > current.count) ? prev : current).obj;
        }
        return null;
    };

    /**
     * Get the count of similar elements between two arrays
     * @param arr
     * @param target
     * @returns {number}
     */
    getSimilarElements(arr, target) {
        let count = 0
        arr.forEach(el => {
            if (target.includes(el)) {
                count++
            }
        })
        return count;
    }

    /**
     * Create new canvas and return the sugar output
     * @param canvas
     * @returns {HTMLCanvasElement}
     */
    output(canvas) {

        let width = numericSize(this.size);
        let height = numericSize(this.size);

        let newCanvas;

        if (this.renderer !== RENDERER_TYPE_THUMBGEN || (this.renderer === RENDERER_TYPE_THUMBGEN && this.forceOutputScaling)) {
            newCanvas = createCanvas(width, height);
            const ctx = newCanvas.getContext("2d");
            disableCanvasSmoothing(ctx);

            if (canvas.height > 0) {
                let arScale = 1;

                if (canvas.width > canvas.height) {
                    arScale = numericSize(this.size) / canvas.width;
                } else {
                    arScale = numericSize(this.size) / canvas.height;
                }

                width = canvas.width * arScale;
                height = canvas.height * arScale;

                newCanvas.width = width;
                newCanvas.height = height;
            }

            ctx.drawImage(canvas, 0, 0, width, height);
        } else {
            newCanvas = canvas;
        }


        if (typeof this.thumbParams === "object" && this.thumbParams.hasOwnProperty("divId")
            && this.thumbParams.divId !== null && this.thumbParams.divId !== "") {
            newCanvas.id = `mgCanvas-${this.thumbParams.divId}-thumbnail`;
        }

        return newCanvas;
    }

    /**
     * Fetching the svg from EP Service
     * @private
     * @memberOf SugarWrapper
     *
     * @return String
     */
    async getSVG() {
        try {
            const childSkusStr = Object.keys(this.childSkus).map(key => `${key}-${this.childSkus[key]}`).join("-");

            const cacheKey = `svg-${this.sizeId}-0-${childSkusStr}-${this.isLandscape}-${this.defaultFoilColor != null && this.layoutId ? this.layoutId : ''}`;

            const cached = cacheGet(cacheKey);
            if (cached) return cached;

            const getSvgURL = `${assetsCDN(this.env)}/getSvgs/size/${this.size}/brand/${this.brand}/layoutId/${this.layoutId}/layoutVersion/${this.layoutVersion}/sizeId/${this.sizeId}/surface/0/isLandscape/${this.isLandscape}/childSkus/${JSON.stringify(this.childSkus)}`;
            const options = {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                },
            };
            const response = await fetch(getSvgURL, options);

            if (response.status !== 200) {
                error("SugarWrapper:: getSVG", `failed to fetch layout data from ${getSvgURL}`);
            } else {
                const data = await response.json();
                const key = `${this.size}-${this.layoutId}-${this.layoutVersion}-${this.sizeId}-0${childSkusStr !== "" ? `-${childSkusStr}` : ''}`;

                if (data[key]?.svg) {
                    cacheSet(cacheKey, data[key].svg);
                    return data[key].svg;
                } else {
                    error("SugarWrapper:: getSVG", "data was incorrect");
                }

            }
        } catch (e) {
            error("SugarWrapper:: getSVG", `failed to fetch svg from ProductsService - ${e.message}`);
        }
    }
}
